/**
 * Date and Time Utilities for Event Timestamps
 *
 * TIMEZONE HANDLING:
 * - Database stores timestamps in UTC (TIMESTAMPTZ)
 * - API returns ISO 8601 strings with 'Z' suffix (e.g., "2024-02-05T18:00:00.000Z")
 * - These utilities automatically convert UTC to user's local timezone for display
 * - JavaScript's Date object and toLocale* methods handle the conversion
 *
 * Events are stored in the database as full timestamps (TIMESTAMPTZ):
 * - start_time: When the event starts (UTC)
 * - end_time: When the event ends (UTC)
 *
 * These utilities help format timestamps for display and create timestamps from user input.
 */

/**
 * Format a timestamp to a readable date string in user's local timezone
 * @param {string|Date} timestamp - ISO timestamp (UTC) or Date object
 * @returns {string} Formatted date in local timezone (e.g., "Mon, Feb 5, 2024")
 *
 * @example
 * // UTC timestamp "2024-02-05T18:00:00.000Z" displays as:
 * // - "Mon, Feb 5, 2024" in ET (1:00 PM local)
 * // - "Tue, Feb 6, 2024" in JST (3:00 AM next day local)
 */
export const formatEventDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  // The weekday leads, because for anything that repeats it is the part people
  // actually navigate by — "the Tuesday one", not "the 11th". A list of weekly
  // sessions or open court hours read as a column of near-identical dates
  // without it.
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a timestamp to a readable time string in user's local timezone
 * @param {string|Date} timestamp - ISO timestamp (UTC) or Date object
 * @returns {string} Formatted time in local timezone (e.g., "6:00 PM")
 *
 * @example
 * // UTC timestamp "2024-02-05T18:00:00.000Z" displays as:
 * // - "1:00 PM" in ET (UTC-5)
 * // - "3:00 AM" in JST (UTC+9)
 */
export const formatEventTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

/**
 * Format a timestamp to date and time
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Formatted date and time (e.g., "Feb 5, 2024 at 6:00 PM")
 */
export const formatEventDateTime = (timestamp) => {
  if (!timestamp) return '';
  return `${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`;
};

/**
 * Get the date portion of a timestamp in YYYY-MM-DD format
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getDateFromTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD in local timezone
};

/**
 * Get the time portion of a timestamp in HH:MM format
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Time in HH:MM format (24-hour)
 */
export const getTimeFromTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`; // HH:MM in local timezone
};

/**
 * Create a timestamp from date and time strings
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format
 * @returns {string} ISO timestamp string
 */
export const createTimestamp = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const datetime = new Date(`${dateStr}T${timeStr}:00`);
  return datetime.toISOString();
};

/**
 * Create timestamps for an event, handling midnight-crossing
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} startTimeStr - Start time in HH:MM format
 * @param {string} endTimeStr - End time in HH:MM format
 * @returns {{start_time: string, end_time: string}} Timestamp objects
 */
export const createEventTimestamps = (dateStr, startTimeStr, endTimeStr) => {
  if (!dateStr || !startTimeStr || !endTimeStr) return null;

  const startDateTime = new Date(`${dateStr}T${startTimeStr}:00`);
  const endDateTime = new Date(`${dateStr}T${endTimeStr}:00`);

  // Handle midnight-crossing events (endTime < startTime means next day)
  if (endTimeStr < startTimeStr) {
    endDateTime.setDate(endDateTime.getDate() + 1);
  }

  return {
    start_time: startDateTime.toISOString(),
    end_time: endDateTime.toISOString()
  };
};

/**
 * Check if an event has started
 * @param {Object} event - Event object with start_time
 * @returns {boolean} True if event has started
 */
export const hasEventStarted = (event) => {
  if (!event?.start_time) return false;
  return new Date(event.start_time) <= new Date();
};

/**
 * Check if an event has ended
 * @param {Object} event - Event object with end_time
 * @returns {boolean} True if event has ended
 */
export const hasEventEnded = (event) => {
  if (!event?.end_time) return false;
  return new Date(event.end_time) <= new Date();
};

/**
 * Check if an event is currently in progress
 * @param {Object} event - Event object with start_time and end_time
 * @returns {boolean} True if event is in progress
 */
export const isEventInProgress = (event) => {
  return hasEventStarted(event) && !hasEventEnded(event);
};

/**
 * Get time remaining until event starts or ends
 * @param {Object} event - Event object with start_time and end_time
 * @returns {string} Human-readable time remaining
 */
export const getEventTimeRemaining = (event) => {
  if (!event) return '';

  const now = new Date();
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);

  if (now < startTime) {
    // Event hasn't started
    const diff = startTime - now;
    return formatTimeRemaining(diff, 'until start');
  } else if (now < endTime) {
    // Event in progress
    const diff = endTime - now;
    return formatTimeRemaining(diff, 'remaining');
  } else {
    // Event ended
    return 'Event ended';
  }
};

/**
 * Format milliseconds to human-readable time remaining
 * @param {number} ms - Milliseconds
 * @param {string} suffix - Suffix to add (e.g., "until start", "remaining")
 * @returns {string} Formatted time
 */
const formatTimeRemaining = (ms, suffix = '') => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ${suffix}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${suffix}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ${suffix}`;
  } else {
    return `${seconds} second${seconds > 1 ? 's' : ''} ${suffix}`;
  }
};

/**
 * Compare two events by start time for sorting
 * @param {Object} a - First event
 * @param {Object} b - Second event
 * @returns {number} Sort order
 */
export const compareEventsByStartTime = (a, b) => {
  const aTime = new Date(a.start_time).getTime();
  const bTime = new Date(b.start_time).getTime();
  return aTime - bTime;
};

/**
 * Group events by date
 * @param {Array} events - Array of event objects
 * @returns {Object} Events grouped by date
 */
export const groupEventsByDate = (events) => {
  if (!events || events.length === 0) return {};

  return events.reduce((groups, event) => {
    const date = getDateFromTimestamp(event.start_time);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});
};

/**
 * Transform event object from API response
 * Ensures all timestamp fields are properly formatted ISO strings
 * This is a centralized place to handle timezone conversion from API responses
 *
 * @param {Object} event - Event object from API
 * @returns {Object} Transformed event with consistent timestamp format
 *
 * @example
 * const event = transformEventFromAPI(apiResponse);
 * // event.start_time and event.end_time are guaranteed to be ISO strings
 */
export const transformEventFromAPI = (event) => {
  if (!event) return null;

  return {
    ...event,
    // Ensure timestamps are ISO strings (API should already return these as ISO strings)
    // If they're Date objects, convert them
    start_time: event.start_time instanceof Date
      ? event.start_time.toISOString()
      : event.start_time,
    end_time: event.end_time instanceof Date
      ? event.end_time.toISOString()
      : event.end_time,
    createdAt: event.createdAt instanceof Date
      ? event.createdAt.toISOString()
      : event.createdAt,
    updatedAt: event.updatedAt instanceof Date
      ? event.updatedAt.toISOString()
      : event.updatedAt,
  };
};

/**
 * Transform array of events from API response
 * @param {Array} events - Array of event objects from API
 * @returns {Array} Transformed events
 */
export const transformEventsFromAPI = (events) => {
  if (!events || !Array.isArray(events)) return [];
  return events.map(transformEventFromAPI);
};
